import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Env, ENV_COLORS } from '../shared';
import { UserLookupPanel } from './panels/UserLookupPanel';
import { RewardModerationPanel } from './panels/RewardModerationPanel';
import { VenueRewardsPanel } from './panels/VenueRewardsPanel';
import { KillSwitchPanel } from './panels/KillSwitchPanel';
import { EconomyHealthPanel } from './panels/EconomyHealthPanel';
import { RunbookPanel } from './panels/RunbookPanel';
import { GiftCardQueuePanel } from './panels/GiftCardQueuePanel';

type SubTab = 'users' | 'rewards' | 'venue-rewards' | 'gift-cards' | 'kill-switch' | 'economy' | 'runbook';

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'users', label: 'User Lookup' },
  { id: 'rewards', label: 'Reward Moderation' },
  { id: 'venue-rewards', label: 'Venue Rewards' },
  { id: 'gift-cards', label: 'Gift Cards' },
  { id: 'kill-switch', label: 'Kill Switch' },
  { id: 'economy', label: 'Economy Health' },
  { id: 'runbook', label: 'Runbook' },
];

const isSub = (v: string | null): v is SubTab => SUB_TABS.some((t) => t.id === v);

const ENVS: Env[] = ['dev', 'staging', 'prod'];

/**
 * Gamification admin tab (SSA-818) — User Lookup, Reward Moderation, Kill Switch,
 * Economy Health dashboard (SSA-825), and the on-call Runbook (SSA-826). The env
 * selector chooses which backend stage the panels talk to; it defaults to prod
 * so an on-call responder lands on live data.
 */
export function GamificationTab() {
  const [env, setEnv] = useState<Env>('prod');
  // `?sub=` deep-links a panel (the header's Analytics menu opens Economy Health).
  const requestedSub = useSearchParams()[0].get('sub');
  const navigationKey = useLocation().key;
  const [sub, setSub] = useState<SubTab>(isSub(requestedSub) ? requestedSub : 'users');
  useEffect(() => {
    if (isSub(requestedSub)) setSub(requestedSub);
  }, [requestedSub, navigationKey]);

  return (
    <div className="space-y-5">
      {/* Header: sub-nav + env selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                sub === t.id ? 'bg-violet-950/40 text-violet-300 border border-violet-500/60' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Environment</span>
          <div className="flex gap-1">
            {ENVS.map((e) => (
              <button
                key={e}
                onClick={() => setEnv(e)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md border uppercase ${
                  env === e ? ENV_COLORS[e].pill : 'bg-gray-900 border-gray-800 text-gray-500 hover:text-gray-300'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>

      {env === 'prod' && (
        <div className="px-3 py-2 bg-red-950/30 border border-red-900/40 rounded-lg text-xs text-red-300">
          Acting on <b>production</b>. Flag, coin-adjust, and kill-switch actions affect live users.
        </div>
      )}

      {/* Panel */}
      <div>
        {sub === 'users' && <UserLookupPanel env={env} />}
        {sub === 'rewards' && <RewardModerationPanel env={env} />}
        {sub === 'venue-rewards' && <VenueRewardsPanel env={env} />}
        {sub === 'gift-cards' && <GiftCardQueuePanel env={env} />}
        {sub === 'kill-switch' && <KillSwitchPanel env={env} onOpenEconomy={() => setSub('economy')} />}
        {sub === 'economy' && <EconomyHealthPanel env={env} />}
        {sub === 'runbook' && <RunbookPanel />}
      </div>
    </div>
  );
}
